/**
 * GET — Getalbegrip rulebook.
 *
 * Sourced from:
 *  - DEFAULT_POLICY.notation (Getalbegrip in latexFractionModules,
 *    commaDecimalModules)
 *  - DEFAULT_POLICY.distractor.modulePatterns.Getalbegrip
 *  - DEFAULT_POLICY.explanation.moduleRuleRefRequired.Getalbegrip = true
 *  - docs/guides/MODULE_AUTHORING_GUIDE.md §1
 *  - governanceRulesData.RULEBOOK_FILES.get.keyRules
 *  - docs/audits/VISUAL_QUESTION_TYPE_MAPPING_AUDIT_2026.md §4
 *
 * HINT ORDERING — patterns are evaluated TOP-DOWN; first match wins.
 * Always place the most specific pattern BEFORE the generic family-prefix
 * pattern (e.g. `^GET-KOMMA-DEEL` before `^GET-KOMMA-`).
 */

import type { ModuleRulebook } from './types';
export const GET_RULEBOOK: ModuleRulebook = {
  moduleId: 'Getalbegrip',
  shortCode: 'GET',
  displayName: 'Getalbegrip',
  didactics: {
    principles: ['plaatswaarde', 'representatie_breuk_komma_procent', 'visuele_modellen', 'getallenlijn'],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt', 'Reken Zeker'],
    misconceptionDomain: 'get',
    coreSummary: 'Begrip van getallenstructuur: plaatswaarde, breuken, kommagetallen, procenten en hun onderlinge relaties.'
  },
  notation: {
    requireLatexFractions: true,
    requireCommaDecimals: true,
    forbiddenPatterns: [{
      pattern: '\\d+\\.\\d+',
      label: 'Punt als decimaalteken (gebruik komma: 3,14)'
    }]
  },
  content: {
    extraForbiddenVocabByTierBand: {
      basic: ['noemer', 'teller', 'decimaalgetal']
    },
    distractorMisconceptionPattern: 'plaatswaarde|komma|afrond|decimaal|breuk|noemer|teller|macht|wortel|procent|verhouding'
  },
  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: ['plaatswaarde', 'gelijknamig', 'vereenvoudigen', 'omrekenen']
  },
  visuals: {
    preferredAidTypes: ['fractionStrip', 'fraction', 'numberline', 'percentageBar', 'placeValueBlocks', 'squareGrid', 'barModel', 'mapScale', 'thermometer', 'equalSharing', 'splitDiagram', 'column', 'ratio'],
    familyVisualHints: [
    // ── BREUK — fractionStrip first, with specific overrides ─────────────
    // autoExemptEmbedded zorgt dat seeds die de nieuwe interactieve breuk-
    // types gebruiken (fraction_strip_builder, fraction_equivalence_match,
    // bar_model_part, step_solver, grid_shade) niet alsnog een externe
    // fractionStrip-stimulus moeten leveren — die types ZIJN de visual.
    {
      familyIdPattern: '^GET-BREUK-DEEL',
      preferredStimulus: 'barModel',
      preferredSupport: 'fractionStrip',
      autoExemptEmbedded: true,
      note: 'Breuk-van-getal vraagt om strookmodel (whole → deel), niet om losse breuk-pizza. autoExemptEmbedded dekt bar_model_part / step_solver waarvan de renderer zelf het strookmodel is.'
    }, {
      familyIdPattern: '^GET-BREUK-GEMENGD',
      preferredStimulus: 'fractionStrip',
      preferredSupport: 'numberline',
      autoExemptEmbedded: true,
      note: 'Gemengde breuken: fractionStrip toont 1¼ = 5/4, numberline plaatst het. autoExemptEmbedded dekt fraction_strip_builder / fraction_equivalence_match (eigen visueel oppervlak).'
    }, {
      familyIdPattern: '^GET-BREUK-GETALLENLIJN',
      preferredStimulus: 'numberline',
      autoExemptEmbedded: true,
      note: 'Family-doel is letterlijk een getallenlijn. autoExemptEmbedded dekt number_sequence / fraction_strip_builder / comparison_judge / ordering met eigen visueel oppervlak.'
    }, {
      familyIdPattern: '^GET-BREUK-RASTER',
      preferredStimulus: 'squareGrid',
      autoExemptEmbedded: true,
      note: 'Breuken op een raster — kwart, helft, derde op vierkante grid. autoExemptEmbedded dekt grid_shade / fraction_strip_builder (renderer IS de raster-visual).'
    }, {
      familyIdPattern: '^GET-BREUK-',
      preferredStimulus: 'fractionStrip',
      preferredSupport: 'fraction',
      stimulusAllowed: ['fractionStrip', 'fraction', 'numberline'],
      supportAllowed: ['fraction', 'fractionStrip', 'numberline', 'rule'],
      autoExemptEmbedded: true,
      note: 'Breuk-families: fractionStrip primair, fraction (pizza/bar) als support — beide pedagogisch valide voor deel-van-geheel. numberline toegestaan binnen specifieke subfamilies (GETALLENLIJN, GEMENGD). rule-kaart toegestaan als didactisch script-support. autoExemptEmbedded dekt alle nieuwe interactieve breuk-types (fraction_strip_builder, fraction_equivalence_match, bar_model_part, step_solver) — hun renderer IS de breuk-visual.'
    },
    // ── KOMMA — per-bewerking differentiatie ─────────────────────────────
    {
      familyIdPattern: '^GET-KOMMA-LEZEN|^GET-KOMMA-WAARDE',
      preferredStimulus: 'placeValueBlocks',
      preferredSupport: 'squareGrid',
      questionTypeExempt: ['number_sequence', 'ordering'],
      autoExemptEmbedded: true,
      note: 'Kommagetallen lezen/plaatswaarde: blokjes per plek (tienden/honderdsten); squareGrid (10×10 honderdblok) als support voor tienden/honderdsten. Uitzondering: number_sequence binnen deze families gebruikt bij voorkeur numberLineLeaps (visualiseert de stap), en ordering mag numberline gebruiken (positie-op-lijn is de kern). autoExemptEmbedded dekt place_value_build / comma_shift (eigen plaatswaarde-renderer).'
    }, {
      familyIdPattern: '^GET-KOMMA-VERGELIJK|^GET-KOMMA-GETALLENLIJN',
      preferredStimulus: 'numberline',
      autoExemptEmbedded: true,
      note: 'Vergelijken en positie op getallenlijn — getallenlijn is canoniek. autoExemptEmbedded dekt comparison_judge / ordering / number_sequence.'
    }, {
      familyIdPattern: '^GET-KOMMA-AFRONDEN',
      preferredStimulus: 'numberline',
      autoExemptEmbedded: true,
      note: 'Afronden: "welke kant op?" wordt zichtbaar op getallenlijn met buurankers. autoExemptEmbedded dekt estimation_slider / comparison_judge.'
    }, {
      familyIdPattern: '^GET-KOMMA-PLUS|^GET-KOMMA-MIN',
      preferredStimulus: 'column',
      preferredSupport: 'numberline',
      questionTypeExempt: ['number_sequence'],
      autoExemptEmbedded: true,
      note: 'Optellen/aftrekken met komma: kolomopzet houdt komma op één plek; getallenlijn als support voor handig rekenen. Number_sequence-varianten gebruiken bij voorkeur numberLineLeaps. autoExemptEmbedded dekt cijferend_rekenen / step_solver met eigen kolom-oppervlak.'
    }, {
      familyIdPattern: '^GET-KOMMA-KEER',
      preferredStimulus: 'column',
      preferredSupport: 'splitDiagram',
      autoExemptEmbedded: true,
      note: 'Vermenigvuldigen met komma: kolomopzet, split-diagram of comma_shift voor de ×10/×100-shift. autoExemptEmbedded dekt cijferend_rekenen / comma_shift / step_solver.'
    }, {
      familyIdPattern: '^GET-KOMMA-DEEL',
      preferredStimulus: 'equalSharing',
      preferredSupport: 'barModel',
      autoExemptEmbedded: true,
      note: 'Delen met komma: gelijk verdelen-visual maakt "elk krijgt 0,25" tastbaar; comma_shift voor ÷10/÷100-shift. autoExemptEmbedded dekt cijferend_delen / comma_shift / bar_model_part / step_solver.'
    },
    // ── GROOT (positiestelsel) ───────────────────────────────────────────
    {
      familyIdPattern: '^GET-GROOT-POS|^GET-GROOT-LEZEN',
      preferredStimulus: 'placeValueBlocks',
      questionTypeExempt: ['number_sequence', 'ordering'],
      autoExemptEmbedded: true,
      note: 'Plaatswaarde-vragen met MAB-blokjes (D/H/T/E) verankeren positie in concrete blokjes. Sequence/ordering-subtypes mogen numberline gebruiken. autoExemptEmbedded dekt place_value_build (renderer IS de blokjes-builder).'
    }, {
      familyIdPattern: '^GET-GROOT-VERGELIJK',
      preferredStimulus: 'placeValueBlocks',
      preferredSupport: 'numberline',
      questionTypeExempt: ['number_sequence', 'ordering'],
      autoExemptEmbedded: true,
      note: 'Vergelijken: plaats-voor-plaats blokjes, getallenlijn als positie-anker. Voor number_sequence: bij voorkeur numberLineLeaps (toont de stap). Ordering mag numberline. autoExemptEmbedded dekt comparison_judge / place_value_build.'
    }, {
      familyIdPattern: '^GET-GROOT-AFRONDEN|^GET-GROOT-SCHATTEN|^GET-ROND-',
      preferredStimulus: 'numberline',
      preferredSupport: 'placeValueBlocks',
      autoExemptEmbedded: true,
      note: 'Afronden/schatten: numberline toont ankerpunten en richting. autoExemptEmbedded dekt estimation_slider / comparison_judge.'
    },
    // ── PROC (procenten) ─────────────────────────────────────────────────
    {
      familyIdPattern: '^GET-PROC-VAN',
      preferredStimulus: 'percentageBar',
      preferredSupport: 'barModel',
      autoExemptEmbedded: true,
      note: 'Procent van/getal: percentageBar toont %; strookmodel toont deel-van-geheel. autoExemptEmbedded dekt bar_model_part / double_number_line / step_solver (eigen visueel oppervlak).'
    }, {
      familyIdPattern: '^GET-PROC-BTW',
      preferredStimulus: 'percentageBar',
      preferredSupport: 'rule',
      supportAllowed: ['rule', 'barModel'],
      autoExemptEmbedded: true,
      note: 'BTW start met 100% exclusief en 121% inclusief. Gebruik percentageBar voor de opslag; gebruik bij terugrekenen bij voorkeur double_number_line zodat de 100%↔121%-relatie zichtbaar wordt. De rule-kaart is pas steun na dit model, niet het primaire antwoordscript. autoExemptEmbedded dekt bar_model_part / double_number_line / step_solver.'
    }, {
      familyIdPattern: '^GET-PROC-TOENAME|^GET-PROC-AFNAME',
      preferredStimulus: 'percentageBar',
      preferredSupport: 'barModel',
      autoExemptEmbedded: true,
      note: 'Procentuele verandering: balk laat oud→nieuw zien; numberline is hier didactisch zwak. autoExemptEmbedded dekt bar_model_part / double_number_line / step_solver.'
    }, {
      familyIdPattern: '^GET-PROC-SAMENGESTELD',
      preferredStimulus: 'percentageBar',
      preferredSupport: 'numberline',
      autoExemptEmbedded: true,
      note: 'Samengestelde rente: balk per periode; numberline als tijdsas. autoExemptEmbedded dekt double_number_line / step_solver / bar_model_part.'
    }, {
      familyIdPattern: '^GET-PROC-',
      preferredStimulus: 'percentageBar',
      autoExemptEmbedded: true,
      note: 'Procenten via een procenten-balk visualiseren. autoExemptEmbedded dekt alle interactieve types met eigen visueel oppervlak (comparison_judge, ordering, categorize, true_false_series, bar_model_part, …) via QUESTION_TYPE_VISUAL_ROLE.'
    },
    // ── VERH (verhoudingen) ──────────────────────────────────────────────
    {
      familyIdPattern: '^GET-VERH-SCHAAL-KAART',
      preferredStimulus: 'mapScale',
      preferredSupport: 'mapScale',
      stimulusAllowed: ['mapScale', 'ratio'],
      supportAllowed: ['mapScale', 'ratio', 'rule'],
      autoExemptEmbedded: true,
      note: 'Schaal op kaart: mapScale-visual koppelt cm op kaart aan werkelijke afstand — canoniek zowel als stimulus (probleem-anker) als support (worked-example). rule-kaart toegestaan als 1cm=X-km-script-support. autoExemptEmbedded dekt ratio_table / double_number_line / step_solver.'
    }, {
      familyIdPattern: '^GET-VERH-SCHAAL',
      preferredStimulus: 'ratio',
      preferredSupport: 'mapScale',
      stimulusAllowed: ['ratio', 'mapScale'],
      supportAllowed: ['mapScale', 'ratio', 'rule'],
      autoExemptEmbedded: true,
      note: 'Schaal in tekening/blueprint: verhoudingstabel als primary, maar mapScale evenzeer geldig (specifieke -KAART subfamilie). rule-kaart toegestaan als 1%-script-support. autoExemptEmbedded dekt ratio_table / double_number_line / step_solver.'
    }, {
      familyIdPattern: '^GET-VERH-VERDELEN',
      preferredStimulus: 'barModel',
      autoExemptEmbedded: true,
      note: 'Verhoudings-verdelen: strookmodel verdeelt het geheel in proportionele delen. autoExemptEmbedded dekt bar_model_part / step_solver (eigen strookmodel-renderer).'
    }, {
      familyIdPattern: '^GET-VERH-RECEPT',
      preferredStimulus: 'ratio',
      preferredSupport: 'barModel',
      autoExemptEmbedded: true,
      note: 'Recept-opschalen: ratio-tabel toont verhouding ingredient × factor. autoExemptEmbedded dekt ratio_table / double_number_line / bar_model_part.'
    }, {
      familyIdPattern: '^GET-VERH-BREUK-PROC-KOPPEL',
      preferredStimulus: 'percentageBar',
      preferredSupport: 'fractionStrip',
      autoExemptEmbedded: true,
      note: 'Koppeling breuk↔procent↔kommagetal: balk + breuk-strook naast elkaar. autoExemptEmbedded dekt fraction_equivalence_match / double_number_line / bar_model_part.'
    },
    // ── NEGATIEF ─────────────────────────────────────────────────────────
    {
      familyIdPattern: '^GET-NEGATIEF',
      preferredStimulus: 'numberline',
      stimulusAllowed: ['numberline', 'thermometer'],
      supportAllowed: ['thermometer', 'numberline', 'rule'],
      autoExemptEmbedded: true,
      note: 'Negatieve getallen: numberline canoniek als stimulus; thermometer voor concrete context. Support kan thermometer (concreet), numberline (sprongen visualiseren) of rule (didactische regelkaart) zijn — alle drie didactisch geldig. Beide stimuli zijn didactisch geldig — VISUAL_STIMULUS_INCONSISTENT_IN_FAMILY mag de mix niet flaggen. autoExemptEmbedded dekt alle eigen-renderer types (comparison_judge, ordering, number_sequence, thermometer_slider, …) via QUESTION_TYPE_VISUAL_ROLE.'
    },
    // ── MACHT / WORTEL ───────────────────────────────────────────────────
    {
      familyIdPattern: '^GET-WORTEL|^GET-MACHT',
      preferredStimulus: 'squareGrid',
      autoExemptEmbedded: true,
      note: 'Kwadraten en wortels als oppervlakte op een vierkant raster. autoExemptEmbedded dekt alle types met eigen visueel oppervlak (comparison_judge, square_root_builder, ordering, …) via QUESTION_TYPE_VISUAL_ROLE.'
    },
    // ── DEEL (kommagetallen delen) ──────────────────────────────────────
    {
      familyIdPattern: '^GET-DEEL-KOMMA',
      preferredStimulus: 'equalSharing',
      preferredSupport: 'numberline',
      autoExemptEmbedded: true,
      note: 'Delen met komma: gelijk-verdelen visual als primary. autoExemptEmbedded dekt cijferend_delen / comma_shift / step_solver.'
    },
    // ── VERGELIJK (generic) ─────────────────────────────────────────────
    {
      familyIdPattern: '^GET-VERGELIJK',
      preferredStimulus: 'fractionStrip',
      autoExemptEmbedded: true,
      note: 'Algemeen breuken vergelijken: breukenstrook (fractionStrip / fraction-bar) is de canonical pedagogische tool — leerlingen zien direct welk deel groter is. hintEquivalent aliast `fraction` ↔ `fractionStrip`. autoExemptEmbedded dekt comparison_judge / ordering / step_solver via QUESTION_TYPE_VISUAL_ROLE.'
    },
    // ── WETENSCHAPPELIJK ────────────────────────────────────────────────
    {
      familyIdPattern: '^GET-WETENSCHAP',
      preferredStimulus: 'powersOfTenStrip',
      preferredSupport: 'powersOfTenStrip',
      autoExemptEmbedded: true,
      note: 'Wetenschappelijke notatie: machten-van-10-lint (powersOfTenStrip) verankert de plaatswaarde-ladder en markeert de antwoord-exponent. autoExemptEmbedded dekt scientific_notation_builder / comparison_judge / step_solver (eigen visueel oppervlak).'
    }]
  },
  questionTypes: {
    preferredTypes: ['multiple_choice', 'fill_in', 'comparison_judge', 'estimation_slider', 'number_sequence', 'square_root_builder', 'place_value_build', 'bar_model_part', 'fraction_strip_builder', 'fraction_equivalence_match', 'step_solver', 'double_number_line', 'comma_shift', 'scientific_notation_builder'],
    customTypeRoles: [{
      type: 'square_root_builder',
      didacticRole: 'concept',
      preferredDok: 2,
      appropriateTiers: {
        min: 9,
        max: 14
      },
      reason: 'Bouw een vierkant met N×N hokjes — verbindt kwadraat en wortel als oppervlakte-zijde-relatie.'
    }, {
      type: 'place_value_build',
      didacticRole: 'concept',
      preferredDok: 2,
      appropriateTiers: {
        min: 1,
        max: 8
      },
      reason: 'Componeer een getal met MAB-blokjes per plaatswaarde — actief plaatswaarde-begrip i.p.v. MC-keuze.'
    }, {
      type: 'bar_model_part',
      didacticRole: 'strategic',
      preferredDok: 2,
      appropriateTiers: {
        min: 5,
        max: 14
      },
      reason: 'Lees een deel af op het strookmodel — schoolboek-anker voor breuk/procent/verhouding van een geheel.'
    }, {
      type: 'fraction_strip_builder',
      didacticRole: 'concept',
      preferredDok: 2,
      appropriateTiers: {
        min: 4,
        max: 11
      },
      reason: 'Bouw een breukstrook door hokjes te kleuren — verankert teller/noemer-relatie en breukrepresentatie op een geheel.'
    }, {
      type: 'fraction_equivalence_match',
      didacticRole: 'strategic',
      preferredDok: 2,
      appropriateTiers: {
        min: 5,
        max: 12
      },
      reason: 'Koppel gelijkwaardige breuken (½ ↔ 2/4 ↔ 3/6) — actief begrip van uitbreiden/vereenvoudigen i.p.v. losse MC.'
    }, {
      type: 'step_solver',
      didacticRole: 'strategic',
      preferredDok: 2,
      appropriateTiers: {
        min: 5,
        max: 14
      },
      reason: 'Geleide tussenstap-invoer voor meerstaps-bewerkingen (gelijknamig maken → tellers optellen → vereenvoudigen) — maakt strategie expliciet.'
    }, {
      type: 'double_number_line',
      didacticRole: 'strategic',
      preferredDok: 2,
      appropriateTiers: {
        min: 7,
        max: 14
      },
      reason: 'Dubbele getallenlijn — canoniek WiG-model voor procenten van een geheel en verhoudingen. Verbindt referentieschaal (0–100%) met waardeschaal (0–totaal) via proportioneel-redeneren; ankerpunten maken de verhouding zichtbaar.'
    }, {
      type: 'comma_shift',
      didacticRole: 'concept',
      preferredDok: 2,
      appropriateTiers: {
        min: 6,
        max: 10
      },
      reason: 'Komma-verschuiver — maakt de plaatswaarde-shift bij ×10/×100/÷10/÷100 expliciet zichtbaar. Adresseert de hardnekkige misconceptie "×10 = nul erbij" door de cijfers vast te zetten en alleen de komma te laten bewegen. Schoolbook-anker: Pluspunt groep 6–7.'
    }, {
      type: 'scientific_notation_builder',
      didacticRole: 'concept',
      preferredDok: 2,
      appropriateTiers: {
        min: 12,
        max: 16
      },
      reason: 'Wetenschappelijke-notatie bouwer — splitst de twee keuzes (coëfficiënt 1 ≤ a < 10 en exponent 0–8) zodat de canonieke vorm zichtbaar wordt. Verbindt plaatswaarde-ladder aan compacte schrijfwijze en adresseert de "verkeerde coëfficiënt" en "exponent één te ver" misconcepties direct. Schoolbook-anker: groep 8 wetenschappelijke notatie.'
    }],
    familyTypeHints: [{
      familyIdPattern: '^GET-BREUK-(BASIS|LEZEN|RASTER|GETALLENLIJN)',
      preferredTypes: ['multiple_choice', 'fill_in', 'fraction_strip_builder', 'fraction_equivalence_match'],
      discouragedTypes: ['estimation_slider'],
      note: 'Breuk-introductie: visueel afleiden van fractie/raster — exacte tellers/noemers vereist. fraction_strip_builder maakt deel-van-geheel actief, fraction_equivalence_match koppelt representaties. Geen slider-schatting.'
    }, {
      familyIdPattern: '^GET-BREUK-(GEL-PLUS|GEL-MIN|ONG-PLUS|MIN|KEER|DEEL|UITBREIDEN|VEREENVOUDIG|GEMENGD)',
      preferredTypes: ['fill_in', 'multiple_choice', 'bar_model_part', 'step_solver', 'fraction_equivalence_match'],
      discouragedTypes: ['true_false_series', 'estimation_slider'],
      note: 'Breuk-bewerkingen vereisen exact resultaat (teller/noemer). step_solver geleidt de gelijknamig-maken-stap; fraction_equivalence_match traint uitbreiden/vereenvoudigen. Geen waar/onwaar-oordeel of slider.'
    }, {
      familyIdPattern: '^GET-GROOT-(POS|LEZEN|VERGELIJK|AFRONDEN|SCHATTEN)',
      preferredTypes: ['place_value_build', 'fill_in', 'multiple_choice', 'comparison_judge'],
      discouragedTypes: ['estimation_slider'],
      note: 'Grote getallen: plaatswaarde actief componeren of vergelijken — slider verbergt cijfer-positie.'
    }, {
      familyIdPattern: '^GET-KOMMA-(LEZEN|VERGELIJK|AFRONDEN)',
      preferredTypes: ['place_value_build', 'fill_in', 'multiple_choice', 'comparison_judge'],
      discouragedTypes: ['estimation_slider'],
      note: 'Kommagetal-begrip (lezen/vergelijken/afronden): plaatswaarde-blokjes of exacte invoer — niet schatten.'
    }, {
      familyIdPattern: '^GET-KOMMA-WAARDE',
      preferredTypes: ['place_value_build', 'comma_shift', 'fill_in', 'multiple_choice'],
      discouragedTypes: ['estimation_slider'],
      note: 'Komma-plaatswaarde: place_value_build leert de cijfer-positie; comma_shift verbindt plaatswaarde aan ×10/÷10-shift. Geen slider-schatting.'
    }, {
      familyIdPattern: '^GET-KOMMA-(KEER|DEEL)',
      preferredTypes: ['comma_shift', 'fill_in', 'multiple_choice'],
      discouragedTypes: ['estimation_slider', 'true_false_series'],
      note: 'Komma vermenigvuldigen/delen met ×10/×100/÷10/÷100: comma_shift maakt de plaatswaarde-shift letterlijk zichtbaar — addresseert de "nul erbij"-misconceptie direct.'
    }, {
      familyIdPattern: '^GET-KOMMA-(PLUS|MIN)',
      preferredTypes: ['fill_in', 'multiple_choice'],
      discouragedTypes: ['estimation_slider', 'true_false_series'],
      note: 'Komma optellen/aftrekken vereist exact antwoord met juiste komma-positie (uitlijnen onder elkaar).'
    }, {
      familyIdPattern: '^GET-PROC-BTW',
      preferredTypes: ['double_number_line', 'bar_model_part', 'fill_in', 'multiple_choice'],
      discouragedTypes: ['true_false_series'],
      note: 'BTW: maak eerst de relatie zichtbaar: exclusief = 100%, inclusief bij 21% = 121%. Gebruik double_number_line voor terugrekenen en bar_model_part voor deel-geheel; laat een kale ÷1,21-opgave pas volgen na deze begripsstap.'
    }, {
      familyIdPattern: '^GET-PROC-(BASIS|VAN|NIEUW|TERUG|TOENAME|AFNAME|SAMENGESTELD)',
      preferredTypes: ['fill_in', 'multiple_choice', 'bar_model_part', 'double_number_line'],
      discouragedTypes: ['true_false_series'],
      note: 'Procent van/tot geheel: strookmodel, dubbele getallenlijn of exacte berekening — geen oordeels-vraagtype. Dubbele getallenlijn is het WiG-anker voor procent van een geheel.'
    }, {
      familyIdPattern: '^GET-(MACHT|WORTEL)',
      preferredTypes: ['square_root_builder', 'fill_in', 'multiple_choice', 'comparison_judge'],
      discouragedTypes: ['estimation_slider', 'true_false_series'],
      note: 'Macht/wortel: square_root_builder maakt N×N-oppervlakte concreet — geen schatting. comparison_judge ondersteunt redeneer-vragen ("wie schat het scherpst?") en wortel-vergelijken.'
    }, {
      familyIdPattern: '^GET-NEGATIEF',
      preferredTypes: ['number_sequence', 'fill_in', 'multiple_choice', 'comparison_judge'],
      note: 'Negatieve getallen: positie op getallenlijn of vergelijking — sequence/judge maken volgorde expliciet.'
    }, {
      familyIdPattern: '^GET-VERH-',
      preferredTypes: ['fill_in', 'multiple_choice', 'bar_model_part', 'double_number_line', 'ratio_table'],
      discouragedTypes: ['true_false_series'],
      note: 'Verhoudingen: ratio-tabel, strookmodel of dubbele getallenlijn — exact uitrekenen, geen oordeel. ratio_table is het canonieke schoolboek-anker voor verhoudingstabel-invulvragen; dubbele getallenlijn voor recept-opschalen en proportioneel-redeneren.'
    }, {
      familyIdPattern: '^GET-VERGELIJK',
      preferredTypes: ['comparison_judge', 'multiple_choice'],
      note: 'Vergelijken: comparison_judge dwingt kleiner/groter/gelijk keuze i.p.v. tussenstap.'
    }, {
      familyIdPattern: '^GET-WETENSCHAP',
      preferredTypes: ['scientific_notation_builder', 'fill_in', 'multiple_choice', 'comparison_judge', 'step_solver'],
      discouragedTypes: ['estimation_slider', 'true_false_series'],
      note: 'Wetenschappelijke notatie: scientific_notation_builder maakt de twee-keuzes-structuur (coëfficiënt + exponent) en de canonieke-vorm-eis (1 ≤ a < 10) expliciet — kern-anker voor GET-WETENSCHAPPELIJK en GET-WETENSCHAP-REKEN. step_solver voor meerstaps berekeningen met machten van 10. Geen slider-schatting of waar/onwaar-oordeel: het exacte product moet kloppen.'
    }]
  },
  distractorTraps: [{
    id: 'place-value-swap',
    label: 'Plaatswaarde verwisselen',
    description: 'Cijfers in de verkeerde plaatswaarde lezen (b.v. tientallen als eenheden).'
  }, {
    id: 'fraction-denominator-add',
    label: 'Noemers optellen',
    description: 'Bij optellen van breuken zowel teller als noemer optellen i.p.v. gelijknamig maken.'
  }, {
    id: 'comma-shift',
    label: 'Komma één plaats verschoven',
    description: 'Bij omrekenen of vergelijken de komma een plaats te ver schuiven.'
  }],
  keyRules: ['LaTeX-notatie verplicht voor breuken: $\\frac{1}{2}', 'Komma als decimaalteken (niet punt): 3,14 niet 3.14', 'Visuele hulpmiddelen verplicht voor breuk-, komma- en procentfamilies', 'Machten en wortels in LaTeX: $2^3$, $\\sqrt{9}$ — bij voorkeur met squareGrid-visual', 'Plaatswaarde-uitleg expliciet benoemen in feedback; placeValueBlocks bij grote-getal-families', 'Strookmodel (barModel) bij deel-van-geheel: procent, breuk, verhouding', 'numberline NIET als default-visual gebruiken — alleen waar didactisch passend (positie, vergelijken, afronden, negatief)', 'Wetenschappelijke notatie: powersOfTenStrip als visual, scientific_notation_builder als interactief vraagtype, canonieke vorm 1 ≤ a < 10 verplicht', 'Trap-distractors: plaatswaarde verwisselen, noemers optellen, komma verschoven'],
  qualityMessages: {
    explanationDepthRequirement: 'GET explanation must show calculation (e.g., "3 × 4 = 12")',
    distractorMisconceptionRequirement: 'GET distractors should reference misconception (plaatswaarde, komma, afronden)'
  },
  authoringGuideAnchor: '#1-get--getalbegrip'
};